import { FadeIn, FadeInStagger, StaggerContainer, ScaleIn } from "../components/AnimationWrapper";
import { trackEvent } from "../analytics/trackEvent.js";

import NavbarEN from "../components/NavbarEN";
import FooterEN from "../components/FooterEN";
import ScrollToTopButton from "../components/ScrollToTopButton";
import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { StarIcon } from "@tailgrids/icons";

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.75024 13.501C6.33613 13.501 6.0004 13.1651 6.00024 12.751V7.50024H0.75C0.335786 7.50024 0 7.16445 0 6.75024C0 6.33603 0.335786 6.00024 0.75 6.00024H6.00024V0.75C6.00024 0.335786 6.33603 0 6.75024 0C7.16445 0 7.50024 0.335786 7.50024 0.75V6.00024H12.751C13.1651 6.0004 13.501 6.33613 13.501 6.75024C13.501 7.16435 13.1651 7.50008 12.751 7.50024H7.50024V12.751C7.50008 13.1651 7.16435 13.501 6.75024 13.501Z"
      fill="#374151"
    />
  </svg>
);

const MinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M6 11.2498C5.58579 11.2498 5.25 11.5855 5.25 11.9998C5.25 12.414 5.58579 12.7498 6 12.7498V11.9998V11.2498ZM18.0007 12.7498C18.4149 12.7498 18.7507 12.414 18.7507 11.9998C18.7507 11.5855 18.4149 11.2498 18.0007 11.2498V11.9998V12.7498ZM6 11.9998V12.7498H18.0007V11.9998V11.2498H6V11.9998Z"
      fill="#374151"
    />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M4 10l4 4 8-8" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CrossIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M5 5l10 10M15 5l-10 10" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function HomeEN() {
  const [openItem, setOpenItem] = useState(null);
  const heroHeadingRef = useRef(null);

  const toggleItem = (id) => {
    setOpenItem(openItem === id ? null : id);
  };

  const handleCTA = (location) => {
    trackEvent("click_register_button", { ab: "EN-HERO", location });
    trackEvent("conversion", { send_to: "AW-18114124186/1vd0CLTGk6EcEJqzvr1D" });
    window.location.href = "https://app.cv-box.com/#signup";
  };

  const CTAButton = ({ location, variant = "primary", children, className = "" }) => (
    <button
      onClick={() => handleCTA(location)}
      className={`cursor-pointer rounded-full font-bold text-base transition-all duration-300 ${
        variant === "primary"
          ? "py-3.5 px-8 bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-400 hover:to-emerald-500 hover:shadow-lg hover:shadow-green-500/30"
          : variant === "secondary"
          ? "py-3 px-7 bg-white/10 text-white border border-white/20 hover:bg-white/20 font-semibold"
          : "py-3 px-7 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:from-indigo-400 hover:to-indigo-500 hover:shadow-lg hover:shadow-indigo-500/30"
      } ${className}`}
    >
      {children}
    </button>
  );

  // ─── FAQ Data ───
  const faqItems = [
    {
      id: 1,
      question: "What can I do with 100 free credits?",
      answer:
        "Enough to build your profile, run an ATS check, seal your CV, track jobs, generate a compatibility report, and create optimized materials for one application. Plus 20 Job Agent questions for coaching.",
    },
    {
      id: 2,
      question: "How does Job Agent work?",
      answer:
        "Job Agent is your AI career coach. Ask questions about a job posting, get interview prep help, or refine your application strategy. Each pack gives 5 questions for 25 credits.",
    },
    {
      id: 3,
      question: "Is my data secure?",
      answer:
        "Yes. Your CV and profile data are encrypted and stored securely. We never share your information with third parties. You can delete your data at any time.",
    },
    {
      id: 4,
      question: "Can I use CV Box for any job site?",
      answer:
        "Absolutely. Works with LinkedIn, Indeed, Glassdoor, WeWorkRemotely, and any other site where you can paste a job URL.",
    },
    {
      id: 5,
      question: "Do credits expire?",
      answer:
        "No. Once purchased, credits are yours forever. Use them at your own pace — all at once or spread across months.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };

  // ─── Pricing Data ───
  const plans = [
    {
      name: "Free",
      price: 0,
      credits: "100",
      bonus: null,
      totalCredits: "100",
      description: "Perfect to get started",
      badge: null,
      features: [
        "100 free welcome credits",
        "Build & optimize your CV profile",
        "Track unlimited job postings",
        "4 AI actions (25 credits each)",
        "Job Agent chat (5 questions)",
      ],
      cta: "Start for free",
      highlighted: false,
      href: "https://app.cv-box.com/#signup",
    },
    {
      name: "Starter",
      price: 7.5,
      credits: "500",
      bonus: "200",
      totalCredits: "700",
      description: "~10 job applications",
      badge: null,
      features: [
        "500 credits + 200 bonus",
        "~28 AI actions (compatibility, CV, cover letter)",
        "Job Agent: 140 coaching questions",
        "All features included",
        "Credits never expire",
      ],
      cta: "Buy credits",
      highlighted: false,
    },
    {
      name: "Active Seeker",
      price: 15,
      credits: "1,500",
      bonus: "500",
      totalCredits: "2000",
      description: "~30 job applications",
      badge: "Popular",
      features: [
        "1,500 credits + 500 bonus",
        "~80 AI actions",
        "Job Agent: 400 coaching questions",
        "Priority processing",
        "Email delivery of materials",
      ],
      cta: "Buy credits",
      highlighted: true,
    },
    {
      name: "Mega",
      price: 50,
      credits: "3,500",
      bonus: "1,500",
      totalCredits: "5000",
      description: "~75 job applications",
      badge: "Best value",
      features: [
        "3,500 credits + 1,500 bonus",
        "~200 AI actions",
        "Job Agent: 1000 coaching questions",
        "Priority support",
        "Unlimited job tracking",
      ],
      cta: "Buy credits",
      highlighted: false,
    },
  ];

  const formatPrice = (price) => {
    if (price === 0) return "Free";
    return `€${price}`;
  };

  // ─── Steps Data ───
  const steps = [
    {
      image: "/images/features/step-1.webp",
      title: "1. Build your profile",
      description:
        "Upload your CV and let AI parse your professional experience. Check ATS compatibility and generate a base CV — all free.",
    },
    {
      image: "/images/features/step-2.webp",
      title: "2. Track job postings",
      description:
        "Add jobs by URL. Track each application status (New → Crafting → Submitted) in your personal Job Space.",
    },
    {
      image: "/images/features/step-3.webp",
      title: "3. Analyze compatibility",
      description:
        "Get a detailed compatibility report comparing your profile against a specific job — strengths, gaps, and recommendations.",
    },
    {
      image: "/images/features/step-4.webp",
      title: "4. Generate tailored materials",
      description:
        "Create CVs and cover letters optimized for each job posting. Edit and save unlimited versions.",
    },
    {
      image: "/images/features/step-5.webp",
      title: "5. Get AI coaching",
      description:
        "Chat with Job Agent about specific jobs — get interview prep, salary negotiation, and application strategy help.",
    },
  ];

  // ─── Pain Points Data ───
  const painPoints = [
    {
      emoji: "🚫",
      title: "ATS rejects without explanation",
      description:
        "Applicant Tracking Systems filter out resumes before a human ever sees them. Formatting, keywords, and structure mismatches cause silent rejections.",
    },
    {
      emoji: "🌍",
      title: "One CV for every job",
      description:
        "Each job posting has unique keywords and requirements. A generic CV means lower match scores and fewer callbacks.",
    },
    {
      emoji: "❓",
      title: "No feedback on what went wrong",
      description:
        "You apply, wait weeks, and hear nothing. No way to know if it was your CV, your experience, or just bad luck.",
    },
    {
      emoji: "⏱️",
      title: "Hours to tailor each application",
      description:
        "Customizing your CV and writing a cover letter for each role takes hours. Active job seekers apply to 20+ positions.",
    },
    {
      emoji: "😶",
      title: "Silence after applying",
      description:
        "You submit your application and get no response. Did it reach a human, or did it disappear into an ATS black hole?",
    },
  ];

  // ─── Pipeline Steps Data ───
  const pipelineSteps = [
    { number: 1, title: "📄 Upload your profile", description: "Upload your CV as PDF or fill in your profile manually — AI will parse your professional experience in seconds" },
    { number: 2, title: "🔍 ATS compatibility check", description: "Find out if your CV passes automated screening in ATS systems like Greenhouse, Lever, and Workday" },
    { number: 3, title: "✅ Seal your base CV", description: "Review the generated ATS-friendly CV in the markdown editor and seal it as the foundation for all applications" },
    { number: 4, title: "🎯 Add a job posting", description: "Paste a job URL — the system automatically extracts requirements, keywords, skills, and employer expectations" },
    { number: 5, title: "📊 Compatibility report", description: "Get a detailed analysis: your strengths, experience gaps, and specific recommendations on what to add" },
    { number: 6, title: "✍️ CV + Cover Letter", description: "Generate a CV and cover letter optimized for the specific keywords and requirements of that job posting" },
    { number: 7, title: "🎤 Interview preparation", description: "Get a personalized guide for any stage — HR call, technical interview, or meeting with a hiring manager. Be prepared at every step" },
  ];

  // ─── Competitor Comparison Data ───
  const featureDimensions = [
    "ATS optimization",
    "Per-job CV tailoring",
    "Cover letter",
    "Compatibility scoring",
    "Job tracking",
    "AI coaching",
  ];

  const competitors = [
    { name: "CV Box", highlighted: true, features: [true, true, true, true, true, true] },
    { name: "resume.io", highlighted: false, features: [true, false, false, false, false, false] },
    { name: "Enhancv", highlighted: false, features: [true, false, false, false, false, false] },
    { name: "Rezi", highlighted: false, features: [true, true, false, false, false, false] },
    { name: "Teal", highlighted: false, features: [true, true, true, true, true, false] },
    { name: "Jobscan", highlighted: false, features: [true, false, false, true, false, false] },
    { name: "Kickresume", highlighted: false, features: [false, false, true, false, false, false] },
    { name: "LinkedIn", highlighted: false, features: [false, false, false, false, true, false] },
  ];

  // ─── Platform Features Data ───
  const platformFeatures = [
    {
      emoji: "📋",
      title: "ATS-compliant format",
      description:
        "Your CV is generated in a structure that Workday, Greenhouse, and Lever parse correctly — proper dates, sections, and encoding.",
    },
    {
      emoji: "🎯",
      title: "Keywords from job descriptions",
      description:
        "AI extracts keywords from the job posting and integrates them into your CV — ATS sees an exact match between your experience and requirements.",
    },
    {
      emoji: "🌍",
      title: "Tailored for each company",
      description:
        "Product company, startup, or enterprise — each expects different emphasis. CV Box adapts your experience presentation for the specific employer type.",
    },
  ];

  // ─── Testimonials Data ───
  const testimonials = [
    {
      name: "Alex Johnson",
      role: "Product Manager",
      location: "San Francisco, USA",
      quote: "Applied to 200+ jobs on LinkedIn with my generic CV — no luck. CV Box showed me how to tailor for each role. Within a month, I got 3 offers.",
      image: "/images/testimonial/avatar-1.webp",
    },
    {
      name: "Priya Sharma",
      role: "Software Engineer",
      location: "Bengaluru, India",
      quote: "50+ applications, zero callbacks. I was stuck. CV Box revealed the ATS issues in my CV. Optimized version got me 4 interview calls in one week.",
      image: "/images/testimonial/avatar-2.webp",
    },
    {
      name: "Marcus Weber",
      role: "DevOps Engineer",
      location: "Berlin, Germany",
      quote: "The compatibility report showed me exactly what I was missing for each role. Stopped wasting time on jobs I wasn't qualified for.",
      image: "/images/testimonial/avatar-3.webp",
    },
    {
      name: "Sarah Chen",
      role: "Data Analyst",
      location: "London, UK",
      quote: "Cover letters used to take me an hour each. Now it's 2 minutes and they're actually personalized to the company. Game changer.",
      image: "/images/testimonial/avatar-4.webp",
    },
    {
      name: "Olena Kovalenko",
      role: "Freelance Designer",
      location: "Kyiv, Ukraine",
      quote: "Every client had different requirements. CV Box helped me create customized versions in minutes. My response rate doubled!",
      image: "/images/testimonial/avatar-5.webp",
    },
  ];

  return (
    <>
      <NavbarEN heroHeadingRef={heroHeadingRef} />
      <Helmet>
        <html lang="en" />
        <title>CV Box – AI CV Optimizer & ATS Resume Builder | Tailored CVs for Every Job</title>
        <meta name="description" content="Create ATS-optimized resumes tailored to each job posting. Free ATS check + 100 welcome credits. Generate CVs, cover letters & compatibility reports in seconds." />
        
        {/* Canonical & hreflang */}
        <link rel="canonical" href="https://cv-box.com/en" />
        <link rel="alternate" href="https://cv-box.com/en" hrefLang="en" />
        <link rel="alternate" href="https://cv-box.com/ua" hrefLang="uk" />
        <link rel="alternate" href="https://cv-box.com/en" hrefLang="x-default" />
        
        {/* Robots */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        
        {/* Open Graph */}
        <meta property="og:site_name" content="CV Box" />
        <meta property="og:title" content="CV Box – AI CV Optimizer & ATS Resume Builder" />
        <meta property="og:description" content="Create ATS-optimized resumes tailored to each job posting. Free ATS check + 100 welcome credits. Generate CVs, cover letters & compatibility reports in seconds." />
        <meta property="og:image" content="https://cv-box.com/images/og/cv-box-en.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="CV Box — AI-powered CV optimizer for job seekers" />
        <meta property="og:url" content="https://cv-box.com/en" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:locale:alternate" content="uk_UA" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CV Box – AI CV Optimizer & ATS Resume Builder" />
        <meta name="twitter:description" content="AI-powered platform for job seekers. ATS check, CV & cover letter generation tailored to each job. Start for free." />
        <meta name="twitter:image" content="https://cv-box.com/images/og/cv-box-en.png" />
        <meta name="twitter:image:alt" content="CV Box — AI-powered CV optimizer for job seekers" />
        
        {/* Keywords */}
        <meta name="keywords" content="resume, CV, ATS, resume optimizer, job search, cover letter, AI resume, ATS resume builder, tailored CV, job application, career tools, resume builder" />
      </Helmet>

      {/* Structured Data — SoftwareApplication */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "CV Box",
          "url": "https://cv-box.com/en",
          "description": "AI-powered platform for CV optimization. ATS compatibility check, tailored CV & cover letter generation for each job posting, compatibility reports, and interview preparation.",
          "applicationCategory": "BusinessApplication",
          "applicationSubCategory": "Career Management Software",
          "operatingSystem": "Web",
          "inLanguage": "en",
          "offers": [
            { "@type": "Offer", "name": "Free", "price": "0", "priceCurrency": "EUR", "description": "100 free welcome credits" },
            { "@type": "Offer", "name": "Starter", "price": "7.50", "priceCurrency": "EUR", "description": "700 credits — ~10 applications" },
            { "@type": "Offer", "name": "Active Seeker", "price": "15", "priceCurrency": "EUR", "description": "2000 credits — ~30 applications" },
            { "@type": "Offer", "name": "Mega", "price": "50", "priceCurrency": "EUR", "description": "5000 credits — ~75 applications" }
          ],
          "featureList": [
            "ATS-optimized CV generation",
            "Per-job CV tailoring",
            "Cover letter generator",
            "Job compatibility scoring",
            "AI career coaching (Job Agent)",
            "Job application tracking",
            "Interview preparation"
          ],
          "audience": { "@type": "Audience", "audienceType": "Job seekers and professionals" }
        })}</script>
      </Helmet>

      {/* ═══════════════════════════════════════════════════════════════════
          Section 1: Hero — split layout, text left / image right, full-height
      ═══════════════════════════════════════════════════════════════════ */}
      <main>
      <section className="bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] relative overflow-hidden min-h-screen flex items-center">
        {/* Subtle gradient glow */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.12)_0%,transparent_70%)] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[radial-gradient(ellipse_at_center,rgba(52,211,153,0.08)_0%,transparent_70%)] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 w-full py-24 lg:py-0">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
            {/* LEFT — Text */}
            <div className="lg:w-5/12 text-center lg:text-left">
              <FadeInStagger>
                {/* Headline */}
                <FadeIn delay={0.1}>
                  <h1 ref={heroHeadingRef} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
                    Land your next role with{" "}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-green-400">
                      AI-powered precision
                    </span>
                  </h1>
                </FadeIn>

                {/* Subtitle */}
                <FadeIn delay={0.2}>
                  <p className="text-white/60 text-lg lg:text-xl max-w-lg mb-8 leading-relaxed">
                    Build your profile once — get a tailored CV, cover letter, and interview prep for every job you apply to. In minutes, not hours.
                  </p>
                </FadeIn>

                {/* CTA */}
                <FadeIn delay={0.3}>
                  <div className="flex gap-4 items-center flex-col sm:flex-row lg:justify-start justify-center">
                    <CTAButton location="hero" variant="primary">
                      Start for free — 100 credits
                    </CTAButton>
                  </div>
                </FadeIn>
              </FadeInStagger>
            </div>

            {/* RIGHT — Product image (larger, pushed right) */}
            <FadeIn delay={0.3} className="lg:w-7/12 lg:-mr-16 xl:-mr-24">
              <div className="relative">
                <div className="p-2 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm shadow-2xl shadow-indigo-500/10">
                  <img
                    src="/images/covers/cv-hero-image.webp"
                    className="w-full rounded-xl"
                    alt="CV Box — build your profile, track jobs, and use AI career tools"
                    fetchPriority="high"
                    width={1401}
                    height={747}
                  />
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          Section 2: Pain Points
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <FadeIn>
            <h2 className="text-4xl lg:text-5xl font-medium text-gray-900 text-center mb-16 -tracking-[1.9px]">
              The worst part of job searching isn&#39;t rejection — it&#39;s silence
            </h2>
          </FadeIn>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {painPoints.map((card, index) => (
              <FadeIn key={index} className="bg-white rounded-2xl p-6 shadow-feature-card">
                <span className="text-4xl mb-4 block">{card.emoji}</span>
                <h3 className="text-2xl font-medium text-gray-900 mb-2">{card.title}</h3>
                <p className="text-base text-gray-500">{card.description}</p>
              </FadeIn>
            ))}
          </StaggerContainer>

          <div className="text-center mt-12">
            <CTAButton location="pain-points">Start your CV for free</CTAButton>
            <p className="text-gray-500 text-base mt-4">
              CV Box solves each of these problems — in five simple steps 👇
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          Section 3: Steps
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="bg-gray-50 py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <FadeIn>
              <h2 className="text-3xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                5 steps to your winning CV
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-lg text-gray-500">
                Five simple steps to turn any job posting into a personalized application that passes automated screening.
              </p>
            </FadeIn>
          </div>

          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, index) => (
              <FadeIn key={index} className="bg-white rounded-2xl p-3 shadow-feature-card">
                <img src={step.image} className="rounded-xl w-full" alt={step.title} loading="lazy" width={741} height={648} />
                <div className="p-5">
                  <h3 className="text-xl font-medium text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-base text-gray-500">{step.description}</p>
                </div>
              </FadeIn>
            ))}
          </StaggerContainer>

          <div className="text-center mt-12">
            <CTAButton location="steps">Try it for free</CTAButton>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          Section: Pipeline — 7-step per-job tailoring
      ═══════════════════════════════════════════════════════════════════ */}
      <section id="features" className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <FadeIn>
              <h2 className="text-3xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                How CV Box optimizes for each job
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-lg text-gray-500">
                From one profile — to personalized materials for every position
              </p>
            </FadeIn>
          </div>

          <div className="max-w-2xl mx-auto">
            <StaggerContainer className="flex flex-col">
              {pipelineSteps.map((step, i) => (
                <FadeIn key={i}>
                  <div className="flex gap-5 items-start">
                    {/* Left: number + vertical line */}
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 rounded-full bg-indigo-500 text-white flex items-center justify-center text-lg font-bold shrink-0">
                        {step.number}
                      </div>
                      {i < pipelineSteps.length - 1 && (
                        <div className="w-0.5 h-16 bg-indigo-200 mt-2"></div>
                      )}
                    </div>
                    {/* Right: content */}
                    <div className="pb-8">
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">{step.title}</h3>
                      <p className="text-base text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </StaggerContainer>
          </div>

          <div className="text-center mt-12">
            <CTAButton location="pipeline">Start optimizing your CV</CTAButton>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          Section: Competitor Comparison Table
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <FadeIn>
              <h2 className="text-3xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                Why CV Box over alternatives?
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-lg text-gray-500">
                Compare features — and see the difference
              </p>
            </FadeIn>
          </div>

          <FadeIn>
            <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-feature-card">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-4 px-4 text-base font-medium text-gray-500 bg-gray-50 sticky left-0 z-10 min-w-[180px]">
                      Feature
                    </th>
                    {competitors.map((comp, i) => (
                      <th
                        key={i}
                        className={`py-4 px-3 text-center text-sm font-semibold min-w-[100px] ${
                          comp.highlighted
                            ? "bg-indigo-50 text-indigo-700"
                            : "text-gray-900"
                        }`}
                      >
                        {comp.name}
                        {comp.highlighted && (
                          <span className="block text-xs font-normal text-indigo-500 mt-0.5">✨ all-in-one</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {featureDimensions.map((dim, fi) => (
                    <tr key={fi} className={fi < featureDimensions.length - 1 ? "border-b border-gray-100" : ""}>
                      <td className="py-3.5 px-4 text-base text-gray-700 font-medium bg-gray-50 sticky left-0 z-10">
                        {dim}
                      </td>
                      {competitors.map((comp, ci) => (
                        <td
                          key={ci}
                          className={`py-3.5 px-3 ${comp.highlighted ? "bg-indigo-50/50" : ""}`}
                        >
                          <div className="flex justify-center">
                            {comp.features[fi] ? <CheckIcon /> : <CrossIcon />}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeIn>

          <div className="text-center mt-12">
            <CTAButton location="competitor-compare">Try CV Box for free</CTAButton>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          Section 4: Platform Features
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <FadeIn>
              <h2 className="text-4xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                Built for how modern ATS systems work
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-lg text-gray-500">
                CV Box solves three key format and content problems
              </p>
            </FadeIn>
          </div>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {platformFeatures.map((feature, index) => (
              <FadeIn key={index} className="bg-white rounded-2xl p-6 shadow-feature-card">
                <span className="text-4xl mb-4 block">{feature.emoji}</span>
                <h3 className="text-2xl font-medium text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-base text-gray-500">{feature.description}</p>
              </FadeIn>
            ))}
          </StaggerContainer>

          <div className="text-center mt-12">
            <CTAButton location="platform-features">Check your CV for free</CTAButton>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          Section 5: Testimonials
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-10 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <FadeIn>
              <h2 className="text-4xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                Here&#39;s what people say after landing their offers
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-lg text-gray-500">
                Real stories from job seekers who changed their approach
              </p>
            </FadeIn>
          </div>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <FadeIn key={i} className="bg-white p-6 rounded-xl shadow-feature-card flex flex-col justify-between h-full">
                <div>
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, si) => (
                      <StarIcon key={si} className="text-yellow-400" size={13} fill="currentColor" />
                    ))}
                  </div>
                  <p className="text-gray-700 text-lg">&quot;{t.quote}&quot;</p>
                </div>
                <div className="mt-6 flex gap-3 items-center">
                  <img className="size-11 rounded-lg" src={t.image} alt={t.name} loading="lazy" width={44} height={44} />
                  <div>
                    <p className="text-gray-900 text-base font-medium">{t.name}</p>
                    <span className="text-sm text-gray-500">{t.role}, {t.location}</span>
                  </div>
                </div>
              </FadeIn>
            ))}
          </StaggerContainer>

          <div className="text-center mt-12">
            <CTAButton location="testimonials">Build your CV</CTAButton>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          Section 6: Pricing
      ═══════════════════════════════════════════════════════════════════ */}
      <section id="pricing" className="bg-gray-50 py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <FadeIn>
              <h2 className="text-4xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
                Simple credit packs
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-lg text-gray-500">
                One-time purchase. Credits never expire. Each AI action costs 25 credits.
              </p>
            </FadeIn>
          </div>

          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <FadeIn
                key={index}
                className={`bg-white p-6 rounded-2xl flex flex-col h-full relative ${
                  plan.highlighted ? "ring-2 ring-green-500 shadow-lg shadow-green-500/10" : "shadow-feature-card"
                }`}
              >
                {plan.badge && (
                  <span className="inline-flex absolute top-5 right-5 text-xs bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-700 items-center h-6 rounded-full px-2.5 py-1 font-medium">
                    {plan.badge}
                  </span>
                )}
                <div>
                  <span className="inline-flex items-center h-7 rounded-full px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium mb-4">
                    {plan.name}
                  </span>
                  <p className="text-base text-gray-600 mb-6">{plan.description}</p>
                  <h3 className="text-5xl font-bold text-gray-900 mb-3">{formatPrice(plan.price)}</h3>
                  <div className="mb-8">
                    {plan.bonus ? (
                      <div className="space-y-1">
                        <div className="text-base text-gray-600">{plan.credits} credits</div>
                        <div className="text-base text-green-600 font-semibold">+ {plan.bonus} bonus</div>
                        <div className="text-xl font-bold text-gray-900 border-t border-gray-200 pt-2 mt-2">
                          = {plan.totalCredits} credits
                        </div>
                      </div>
                    ) : (
                      <span className="text-xl font-bold text-gray-900">{plan.totalCredits} credits</span>
                    )}
                  </div>
                  <ul className="py-6 border-t border-dashed border-gray-200 space-y-3">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex gap-2.5 items-start text-base text-gray-700">
                        <span className="w-5 h-5 rounded-full bg-green-500 inline-flex items-center justify-center shrink-0 mt-0.5">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 10 10" fill="none">
                            <path d="M8.37605 2.7251L3.82605 7.2751L1.62396 5.07304" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto pt-4">
                  <button
                    onClick={() => handleCTA(`pricing-${plan.name.toLowerCase()}`)}
                    className={`w-full cursor-pointer rounded-full font-bold text-base py-3.5 px-6 transition-all duration-300 ${
                      plan.highlighted
                        ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-400 hover:to-emerald-500 hover:shadow-lg hover:shadow-green-500/30"
                        : "bg-gray-900 text-white hover:bg-gray-800 hover:shadow-lg"
                    }`}
                  >
                    {plan.cta}
                  </button>
                </div>
              </FadeIn>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          Section 7: FAQ
      ═══════════════════════════════════════════════════════════════════ */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <section id="faq" className="py-10 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col gap-10 lg:flex-row justify-between">
            {/* Left Column */}
            <FadeIn className="lg:w-5/12">
              <h2 className="text-4xl lg:text-5xl mb-10 lg:mb-20 -tracking-[1.9px] font-medium text-gray-900">
                Frequently asked questions
              </h2>

              <div className="p-5 sm:max-w-xs rounded-2xl bg-gray-50">
                <div className="flex mb-2 -space-x-3 items-center">
                  <img src="/images/users/d-long.webp" className="size-9 rounded-full ring-2 ring-white" alt="User 1" loading="lazy" />
                  <img src="/images/users/j-sf-c.webp" className="size-9 rounded-full ring-2 ring-white" alt="User 2" loading="lazy" />
                  <img src="/images/users/j-cheng-c.webp" className="size-9 rounded-full ring-2 ring-white" alt="User 3" loading="lazy" />
                </div>
                <p className="mb-6 text-gray-700">
                  Have more questions? Reach out to our friendly support team
                </p>
                <a
                  href="/en/contact"
                  className="py-3 px-6 rounded-full font-bold text-base bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-400 hover:to-emerald-500 hover:shadow-lg hover:shadow-green-500/30 transition-all duration-300 inline-block"
                >
                  Contact us
                </a>
              </div>
            </FadeIn>

            {/* Right Column - FAQ Items */}
            <FadeIn delay={0.2} className="lg:w-6/12">
              <div>
                {faqItems.map((item) => (
                  <div key={item.id} className="py-6 border-b border-gray-200">
                    <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleItem(item.id)}>
                      <h3 className="text-lg font-medium text-gray-900 -tracking-[0.18px]">{item.question}</h3>
                      <button className="text-gray-950 shrink-0 ml-4 cursor-pointer" aria-label={openItem === item.id ? "Collapse answer" : "Expand answer"}>
                        {openItem === item.id ? <MinusIcon /> : <PlusIcon />}
                      </button>
                    </div>
                    <AnimatePresence initial={false}>
                      {openItem === item.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          style={{ overflow: "hidden" }}
                        >
                          <p className="mt-4 text-gray-700">{item.answer}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          Section 8: CTA
      ═══════════════════════════════════════════════════════════════════ */}
      <section
        className="pt-28 pb-10 bg-cover bg-center bg-no-repeat relative"
        style={{
          background: "url('/images/covers/hero-background.webp') no-repeat center center / cover",
        }}
      >
        <div className="bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#fff_100%)] absolute w-full h-25 bottom-0 z-20"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-30">
          <ScaleIn className="max-w-2xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl -tracking-[1.9px] mb-4 font-medium text-gray-900">
              Ready to get interview invitations?
            </h2>
            <p className="text-base text-gray-500 mb-9">
              Join hundreds of professionals who&#39;ve changed their job search approach
            </p>
            <div className="flex gap-4 justify-center sm:items-center flex-col sm:flex-row">
              <CTAButton location="cta-bottom">Start your first CV for free</CTAButton>
            </div>
            <p className="text-gray-500 text-sm mt-3">
              First 100 credits — free, no card required
            </p>
          </ScaleIn>
        </div>
      </section>
      </main>
      <ScrollToTopButton />
      <FooterEN />
    </>
  );
}
